import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { CalendarBlank, Warning, Fire, Info } from "@phosphor-icons/react";

const IMPACT_STYLE = {
  high:   { dots: 3, cls: "text-red-500",    bg: "bg-red-500/10 border-red-500/40" },
  medium: { dots: 2, cls: "text-amber-500",  bg: "bg-amber-500/10 border-amber-500/40" },
  low:    { dots: 1, cls: "text-blue-500",   bg: "bg-blue-500/10 border-blue-500/40" },
};

const COUNTRY_FLAG = {
  "United States":  "🇺🇸", "USA": "🇺🇸", "US": "🇺🇸",
  "Euro Area":      "🇪🇺", "European Union": "🇪🇺", "EU": "🇪🇺",
  "United Kingdom": "🇬🇧", "UK": "🇬🇧",
  "Japan":          "🇯🇵",
  "India":          "🇮🇳",
  "China":          "🇨🇳",
  "Canada":         "🇨🇦",
  "Australia":      "🇦🇺",
  "Germany":        "🇩🇪",
};

// Formats "6h 12m 04s" from a positive `secs` count. If secs < 60 it's just "0m 42s".
function formatCountdown(secs) {
  if (secs <= 0) return "LIVE";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

function EventCard({ e, now }) {
  const secs = Math.max(0, e.unix - Math.floor(now / 1000));
  const isImminent = secs <= 3600;                 // ≤1h → red glow
  const isSoon     = secs <= 8 * 3600 && secs > 3600;  // ≤8h → amber glow
  const style = IMPACT_STYLE[e.importance] || IMPACT_STYLE.low;

  const glowCls = isImminent
    ? "border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.35)]"
    : isSoon
      ? "border-amber-500/70 shadow-[0_0_18px_rgba(245,158,11,0.25)]"
      : "border-border";

  const timeStr = new Date(e.when).toLocaleTimeString(undefined,
    { hour: "2-digit", minute: "2-digit", timeZoneName: "short" });

  return (
    <div
      data-testid={`event-card-${e.ticker || e.event.slice(0,20)}`}
      className={`shrink-0 min-w-[300px] rounded-xl border bg-card p-3 transition-all ${glowCls}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-base leading-none" aria-hidden>
            {COUNTRY_FLAG[e.country] || "🌐"}
          </span>
          <p className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground truncate">
            {e.country}
          </p>
          {/* Impact dots */}
          <span className={`ml-1 flex items-center gap-0.5 ${style.cls}`} title={`${e.importance} impact`}>
            {[0, 1, 2].map(i => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i < style.dots ? "bg-current" : "bg-current/20"}`} />
            ))}
          </span>
        </div>
        <div className={`text-[10px] font-mono font-bold tracking-wider px-2 py-0.5 rounded-full border ${style.bg} ${style.cls}`}>
          {isImminent ? <Fire size={10} className="inline mr-1" /> : isSoon ? <Warning size={10} className="inline mr-1" /> : null}
          {formatCountdown(secs)}
        </div>
      </div>

      <p className="text-sm font-semibold mt-2 leading-tight line-clamp-2" title={e.event}>
        {e.event}
      </p>

      <div className="mt-2 flex items-center justify-between text-[10px] font-mono">
        <span className="text-muted-foreground">{timeStr}{e.reference ? ` · ${e.reference}` : ""}</span>
        <div className="flex items-center gap-2 text-muted-foreground">
          {e.forecast && <span>F: <span className="text-foreground font-semibold">{e.forecast}</span></span>}
          {e.previous && <span>P: <span className="text-foreground font-semibold">{e.previous}</span></span>}
        </div>
      </div>
    </div>
  );
}

// Displays the next N upcoming economic events with live-ticking countdowns.
// Refetches from the backend every 5 minutes; countdown re-renders every second.
export default function EventTimer({ hours = 48, limit = 5, impact = "all" }) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Ticker for the countdown display
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch + refetch every 5 minutes
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get(`/market/events?hours=${hours}&limit=${limit}&impact=${impact}`)
        .then(({ data }) => { if (!cancelled) { setPayload(data); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Events unavailable"); });
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [hours, limit, impact]);

  const events = payload?.events || [];
  const nextImminent = useMemo(
    () => events.find(e => (e.unix - Math.floor(now / 1000)) <= 8 * 3600) || null,
    [events, now],
  );

  if (error && !events.length) return null; // silent fail — don't clutter chart page
  if (!payload) {
    return (
      <div className="rounded-xl border border-border bg-card p-3 flex items-center gap-3">
        <CalendarBlank size={16} className="text-muted-foreground" />
        <p className="text-xs text-muted-foreground">Loading upcoming economic events…</p>
      </div>
    );
  }
  if (!events.length) {
    return (
      <div className="rounded-xl border border-border bg-card p-3 flex items-center gap-3" data-testid="event-timer-empty">
        <CalendarBlank size={16} className="text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          No scheduled economic events in the next {hours}h. Enjoy the calm.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="event-timer">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarBlank size={14} className="text-primary" />
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Upcoming Economic Events</p>
          {nextImminent && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono text-amber-500">
              <Warning size={10} /> {nextImminent.event.slice(0, 40)}{nextImminent.event.length > 40 ? "…" : ""} within 8h
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <Info size={11} /> Next {payload.window_hours}h · {payload.source === "trading-economics" ? "Trading Economics" : "cached"}
        </div>
      </div>
      <div className="flex items-stretch gap-3 overflow-x-auto hide-scroll pb-1" data-testid="event-timer-strip">
        {events.map(e => <EventCard key={`${e.event}-${e.unix}`} e={e} now={now} />)}
      </div>
    </div>
  );
}
